<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/payouts/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    >
      <div class="mt-16 text-red text-body-big">{{ $t('toolbar.export.taxPdf.title') }}</div>
      <Multiselect
        v-model="taxPdfYear"
        :options="taxPdfYearOptions"
        :label="$t('toolbar.export.taxPdf.year')"
        :can-deselect="false"
        :can-clear="false"
        :searchable="true"
        class="mt-3 mb-5"
      />
      <ExportLink
        :url="taxPdfUrl"
        :filter="taxPdfFilter"
        :label="$t('toolbar.export.taxPdf.label')"
      />
    </export>
  </toolbar>
  <edit-payout-modal
    v-model:open="openEditPayoutModal"
    :edit-payout="editPayout"
    @saved="refetch"
  />

  <div class="ml-12">
    <PayoutStatisticsTable
      :statistics="{
        total: filteredStatistics?.total,
        filtered: filteredStatistics?.filtered,
        selected: selectedPayoutStatistics,
      }"
      :loading="loadingStatistics"
      :admins-are-allowed="true"
    />

    <div v-if="isFinanceAdmin" class="text-body-regular-bold text-table-text-gray uppercase mb-5">
      {{ $t('pages.driver.payoutList.tableHeader') }}
    </div>
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :choosable="true"
        :actions="actions"
        v-model:checked-ids-list="checkedIdsList"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import Multiselect from '@utils/Multiselect.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@components/utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Driver, HydraCollection, Payout } from '@/entities/Entities';
import EditPayoutModal from '@pages/modals/EditPayoutModal.vue';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import { useQueryRef } from '@/hooks/url';
import { Reward } from '@/entities/Entities';
import { useStore } from '@/store';
import PayoutStatisticsTable, { Statistics } from '@utils/PayoutStatisticsTable.vue';
import { useTableFields } from '@hooks/table-configuration';
import { omit } from 'lodash';
import { calculateSelectedPayoutStatistics } from '@/hooks/payout';

interface Props {
  modelValue: Driver | null;
}

const props = defineProps<Props>();

const auth = useStore();
const isFinanceAdmin = computed(() => auth.getters['auth/IS_FINANCE_ADMIN']);

const checkedIdsList = ref<string[]>([]);

const checkedPayouts = computed(
  () => records.value?.filter((payout: Payout) => checkedIdsList.value.includes(payout.id)) ?? []
);

const selectedPayoutStatistics = calculateSelectedPayoutStatistics(checkedPayouts);

const taxPdfUrl = computed(() => {
  if (props.modelValue) {
    return `/drivers/${props.modelValue.id}/tax-pdf`;
  }
  return undefined;
});

const taxPdfYearOptions = computed(() => {
  const thisYear = new Date().getFullYear();
  const firstYear = props.modelValue?.yearOfFirstPayout
    ? new Date(props.modelValue.yearOfFirstPayout).getFullYear()
    : 2018;
  return Array.from({ length: thisYear - firstYear + 1 }, (_, i) => ({
    value: thisYear - i,
    label: thisYear - i,
  }));
});

const taxPdfYear = ref(new Date().getFullYear());

const taxPdfFilter = computed(() => {
  if (props.modelValue) {
    return {
      year: taxPdfYear.value,
    };
  }
  return undefined;
});

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'executionTimestamp');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const openEditPayoutModal = ref(false);
const editPayout = ref();

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.payoutsFromDriver', {
    driver: props.modelValue?.user?.fullName,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  driver: props.modelValue?.id ?? '',
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.driver.payoutList.actions.edit'),
    action: (item: Payout) => {
      editPayout.value = { ...item };
      openEditPayoutModal.value = true;
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'executionTimestamp',
    title: t('payout.executionDate'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.driver.payoutList.filterGroups.general'),
      options: {
        label: t('payout.executionDate'),
      },
    },
  },
  {
    key: 'paidDate',
    title: t('payout.paidDate'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.driver.payoutList.filterGroups.general'),
      options: {
        label: t('payout.paidDate'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.group.name',
    title: t('pages.driver.payoutList.campaignLabel'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.payoutList.filterGroups.general'),
      options: {
        label: t('pages.driver.payoutList.campaignLabel'),
      },
    },
  },
  {
    key: 'paymentAmount',
    title: t('payout.amount'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'CHF',
    formatter: (v: number) => v.toFixed(2).padStart(5, '0') + ' CHF',
    filter: {
      type: 'range',
      group: t('pages.driver.payoutList.filterGroups.general'),
      options: {
        icon: <span>CHF</span>,
        label: t('payout.amount'),
      },
    },
  },
  {
    key: 'status',
    csvTranslations: {
      pending: t('payoutStatus.pending'),
      inPayment: t('payoutStatus.inPayment'),
      payed: t('payoutStatus.payed'),
      error: t('payoutStatus.error'),
    },
    title: t('payout.status'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`payoutStatus.${item}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driver.payoutList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/PayoutStatus', (item) => ({
          value: item,
          label: t(`payoutStatus.${item}`),
        })),
        label: t('payout.status'),
      },
    },
  },
  {
    key: 'comment',
    title: t('payout.comment'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.payoutList.filterGroups.general'),
      options: {
        label: t('payout.comment'),
      },
    },
  },
  {
    key: 'rewards',
    csvTranslations: {
      top: t('reward.type.top'),
      photo: t('reward.type.photo'),
      focusPoint: t('reward.type.focusPoint'),
    },
    title: t('payout.rewards'),
    group: t('pages.driver.payoutList.fieldGroups.general'),
    sortable: false,
    visible: true,
    formatter: (item: Reward[]) => item.map((r) => t(`reward.type.${r.rewardType}`)).join(', '),
    filter: {
      key: 'rewards.rewardType',
      type: 'dropdown',
      group: t('pages.driver.payoutList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/RewardType', (item) => ({
          value: item,
          label: t(`reward.type.${item}`),
        })),
        label: t('reward.type.DEFAULT'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const blockRequest = computed(() => !props.modelValue);

const {
  data: filteredStatistics,
  loading: loadingStatistics,
  refetch: refetchStatistics,
} = useProvider<Statistics>(
  '/payouts/statistics',
  computed(() => ({
    ...providerParams,
    special: {
      driver: props.modelValue?.id ?? '',
    },
  })),
  {
    disabled: blockRequest,
  }
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Payout>>('/payouts', providerParams, {
  disabled: blockRequest,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
